import { useEffect, useMemo, useState } from "react";
import "../../styles/reviews.css";
import { addMonths } from "../../lib/plChart";
import { jstTodayYmd } from "../../lib/dateRange";
import { isMockMode, fetchReviewOverview, ApiClientError } from "../../api/client";
import type { ReviewListFilter, ReviewOverviewResponse, ReviewPeriodMode, ReviewStoreFilter } from "../../types/reviews";
import { StateBanner } from "../../components/StateBanner";
import { OverviewTab } from "./OverviewTab";
import { ResponseTab } from "./ResponseTab";
import { ReportTab } from "./ReportTab";
import { CampaignsTab } from "./CampaignsTab";

export type ReviewsTab = "overview" | "response" | "report" | "campaigns";

const TABS: { key: ReviewsTab; label: string }[] = [
  { key: "overview", label: "概要" },
  { key: "response", label: "レビュー対応" },
  { key: "report", label: "月次レポート" },
  { key: "campaigns", label: "レビュー依頼施策" },
];

const STORE_OPTIONS: { key: ReviewStoreFilter; label: string }[] = [
  { key: "all", label: "全店舗" },
  { key: "shopify", label: "Shopify" },
  { key: "rakuten", label: "楽天市場" },
  { key: "amazon", label: "Amazon" },
];

const PERIOD_OPTIONS: { key: ReviewPeriodMode; label: string }[] = [
  { key: "month", label: "月次" },
  { key: "quarter", label: "3ヶ月" },
  { key: "year", label: "1年" },
];

/** mockモード（`?mock=1`）のときだけfixtureの基準月（2026-09＝reviewsEngine.ts NOW）に固定する。 */
const FIXTURE_MONTH = "2026-09";

function computeInitialMonth(): string {
  return isMockMode() ? FIXTURE_MONTH : jstTodayYmd().slice(0, 7);
}

interface ReviewsState {
  tab: ReviewsTab;
  period: ReviewPeriodMode;
  month: string;
  store: ReviewStoreFilter;
}

function readUrlState(today: string): ReviewsState {
  const p = new URLSearchParams(window.location.search);
  const tab = (p.get("rv") as ReviewsTab) || "overview";
  const period = (p.get("rvPeriod") as ReviewPeriodMode) || "month";
  return {
    tab: TABS.some((t) => t.key === tab) ? tab : "overview",
    period: period === "quarter" || period === "year" ? period : "month",
    month: p.get("rvMonth") || today,
    store: (p.get("rvStore") as ReviewStoreFilter) || "all",
  };
}

function writeUrlState(s: ReviewsState): void {
  const p = new URLSearchParams(window.location.search);
  p.set("rv", s.tab);
  p.set("rvPeriod", s.period);
  p.set("rvMonth", s.month);
  if (s.store !== "all") p.set("rvStore", s.store);
  else p.delete("rvStore");
  const url = `${window.location.pathname}?${p.toString()}`;
  window.history.replaceState(null, "", url);
}

function monthOptions(today: string): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  for (let i = 0; i < 13; i++) {
    const value = addMonths(today, -i);
    const [y, m] = value.split("-");
    opts.push({ value, label: `${y}年${Number(m)}月` });
  }
  return opts;
}

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

export function ReviewsPage() {
  const [today] = useState<string>(() => computeInitialMonth());
  const [state, setState] = useState<ReviewsState>(() => readUrlState(today));
  const [responseFilter, setResponseFilter] = useState<ReviewListFilter>("unreplied");

  useEffect(() => writeUrlState(state), [state]);

  const set = (patch: Partial<ReviewsState>) => setState((s) => ({ ...s, ...patch }));

  const goToResponse = (filter: ReviewListFilter) => {
    setResponseFilter(filter);
    set({ tab: "response" });
  };

  // 上部バーの「データ更新」表示・低評価件数バッジは全タブ共通のGET reviews/overviewから取る
  // （App.tsx側のサイドバーバッジは別途 low.open+low.wip を単独取得している）。
  const [overview, setOverview] = useState<Loadable<ReviewOverviewResponse>>({ status: "loading" });
  useEffect(() => {
    setOverview({ status: "loading" });
    fetchReviewOverview({ period: state.period, month: state.month, store: state.store })
      .then((data) => setOverview({ status: "ok", data }))
      .catch((e: unknown) =>
        setOverview({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" })
      );
  }, [state.period, state.month, state.store]);

  const updatedLabel = useMemo(() => {
    if (overview.status !== "ok") return "";
    return new Date(overview.data.updatedAt).toLocaleString("ja-JP", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [overview]);

  const unresolvedLow = overview.status === "ok" ? overview.data.low.open + overview.data.low.wip : 0;

  return (
    <div className="rv-root">
      <div className="rv-shell">
        <div className="rv-header rv-no-print">
          <div className="rv-header-inner">
            <div className="rv-logo">
              <span className="rv-logo-word">STOQ</span>
              <span className="rv-logo-sub">レビュー分析</span>
            </div>
            <nav className="rv-tabs">
              {TABS.map((t) => (
                <button key={t.key} className={`rv-tab-btn ${state.tab === t.key ? "active" : ""}`} onClick={() => set({ tab: t.key })}>
                  {t.label}
                  {t.key === "response" && unresolvedLow > 0 && <span className="rv-tab-badge">{unresolvedLow}</span>}
                </button>
              ))}
            </nav>
            <span className="rv-updated">{updatedLabel ? `データ更新 ${updatedLabel}` : ""}</span>
          </div>
        </div>

        <div className="rv-body">
          <div className="rv-filter-bar rv-no-print">
            <div className="rv-segment">
              {PERIOD_OPTIONS.map((o) => (
                <button key={o.key} className={`rv-segment-btn ${state.period === o.key ? "active" : ""}`} onClick={() => set({ period: o.key })}>
                  {o.label}
                </button>
              ))}
            </div>
            <select className="rv-input" value={state.month} onChange={(e) => set({ month: e.target.value })}>
              {monthOptions(today).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <div className="rv-segment">
              {STORE_OPTIONS.map((o) => (
                <button key={o.key} className={`rv-segment-btn ${state.store === o.key ? "active" : ""}`} onClick={() => set({ store: o.key })}>
                  {o.label}
                </button>
              ))}
            </div>
            {overview.status === "ok" && (
              <div className="rv-period-label">
                <span className="rv-period-label-main">{overview.data.period.label}</span>
                <span className="rv-period-label-compare">比較：{overview.data.period.compareLabel}</span>
              </div>
            )}
          </div>

          {state.store === "amazon" ? (
            <div className="rv-card">
              <span className="rv-card-title">Amazon</span>
              <div className="rv-empty">Amazonストアは現在未連携のため、レビューデータがありません。連携が完了次第こちらに表示されます。</div>
            </div>
          ) : (
            <>
              {state.tab === "overview" && (
                <OverviewGate data={overview} onGoToResponse={goToResponse} />
              )}
              {state.tab === "response" && (
                <ResponseTab
                  period={state.period}
                  month={state.month}
                  store={state.store}
                  filter={responseFilter}
                  onFilterChange={setResponseFilter}
                  overview={overview.status === "ok" ? overview.data : null}
                  onGoToFilter={goToResponse}
                />
              )}
              {state.tab === "report" && <ReportTab period={state.period} month={state.month} store={state.store} />}
              {state.tab === "campaigns" && <CampaignsTab month={state.month} />}
            </>
          )}

          <p className="rv-footer-note rv-no-print">
            商品レビュー記入率 ＝ 商品レビューが書かれた注文数 ÷ 期間内注文件数（1注文で複数商品を書かれても1。楽天のショップレビューは含めず別表示）。「商品レビュー」＝楽天の商品レビュー＋Shopifyの全レビュー（ショップレビューは低評価対応フローの対象外）。
          </p>
        </div>
      </div>
    </div>
  );
}

function OverviewGate({
  data,
  onGoToResponse,
}: {
  data: Loadable<ReviewOverviewResponse>;
  onGoToResponse: (filter: ReviewListFilter) => void;
}) {
  if (data.status === "loading") return <StateBanner kind="loading" message="読み込み中" />;
  if (data.status === "error") return <StateBanner kind="error" message={data.message} />;
  return <OverviewTab data={data.data} onGoToResponse={onGoToResponse} />;
}
